import React from 'react';
import Header from '../components/Header';
import { MessageCircle, Mail, Clock, Users, ArrowRight, CheckCircle, Lightbulb, AlertCircle } from 'lucide-react';
import { Seo } from '../src/seo/Seo';

const Support: React.FC = () => {
  // Fonctions par défaut pour les props du Header
  const handleDownloadClick = () => {
    // Rediriger vers la page d'accueil pour le téléchargement
    window.location.href = '/';
  };

  const handleMySpaceClick = () => {
    // Rediriger vers la page d'accueil pour My Space
    window.location.href = '/';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-purple-50">
      <Seo
        title={{ fr: 'Support — Burok', en: 'Support — Burok' }}
        description={{
          fr: "Centre d'aide Burok: réponses rapides, FAQ et contact direct avec notre équipe support.",
          en: 'Burok help center: quick answers, FAQ, and direct contact with our support team.',
        }}
        canonicalPath={{ fr: '/support', en: '/en/support' }}
      />
      <Header onDownloadClick={handleDownloadClick} onMySpaceClick={handleMySpaceClick} />
      
      {/* Hero Section */}
      <section className="px-4 pt-20 pb-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-4xl text-center">
          <div className="inline-flex justify-center items-center mb-6 w-16 h-16 bg-purple-100 rounded-full">
            <MessageCircle className="w-8 h-8 text-purple-600" />
          </div>
          <h1 className="mb-6 text-4xl font-bold text-gray-900 md:text-5xl font-sora">
            Centre d'Aide et Support
          </h1>
          <p className="mx-auto max-w-2xl text-xl text-gray-600 font-rubik">
            Notre équipe est là pour vous accompagner. Trouvez des réponses rapidement ou contactez-nous directement.
          </p>
        </div>
      </section>

      {/* Quick Contact Section */}
      <section className="px-4 pb-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-4xl">
          <div className="p-8 mb-12 bg-white rounded-2xl shadow-xl md:p-12">
            <div className="mb-8 text-center">
              <h2 className="mb-4 text-3xl font-bold text-gray-900 font-sora">
                Contactez-nous
              </h2>
              <p className="text-lg text-gray-600 font-rubik">
                Une question ? Un problème ? Notre équipe support est disponible pour vous aider
              </p>
            </div>

            {/* Main Contact Card */}
            <div className="mb-8">
              <div className="p-8 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl border border-purple-200">
                <div className="text-center">
                  <div className="inline-flex justify-center items-center mb-4 w-16 h-16 bg-purple-100 rounded-full">
                    <Mail className="w-8 h-8 text-purple-600" />
                  </div>
                  <h3 className="mb-3 text-2xl font-bold text-gray-900 font-sora">
                    Email Principal
                  </h3>
                  <p className="mb-6 text-gray-600 font-rubik">
                    Envoyez-nous un email et nous vous répondrons dans les plus brefs délais
                  </p>
                  <a
                    href="mailto:entreprise@burok.app"
                    className="inline-flex items-center px-8 py-4 font-semibold text-white bg-gradient-to-r from-purple-600 to-indigo-600 rounded-full transition-all duration-200 hover:shadow-lg hover:scale-105 font-rubik"
                  >
                    <Mail className="mr-3 w-5 h-5" />
                    entreprise@burok.app
                  </a>
                </div>
              </div>
            </div>

            {/* Support Features Grid */}
            <div className="grid gap-6 mb-8 md:grid-cols-3">
              <div className="p-6 text-center bg-green-50 rounded-xl border border-green-200">
                <div className="inline-flex justify-center items-center mb-4 w-12 h-12 bg-green-100 rounded-full">
                  <Clock className="w-6 h-6 text-green-600" />
                </div>
                <h4 className="mb-2 text-lg font-semibold text-gray-900 font-sora">Réponse Rapide</h4>
                <p className="text-sm text-gray-600 font-rubik">Réponse sous 24h en moyenne</p>
              </div>

              <div className="p-6 text-center bg-blue-50 rounded-xl border border-blue-200">
                <div className="inline-flex justify-center items-center mb-4 w-12 h-12 bg-blue-100 rounded-full">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
                <h4 className="mb-2 text-lg font-semibold text-gray-900 font-sora">Équipe Dédiée</h4>
                <p className="text-sm text-gray-600 font-rubik">Experts Burok à votre service</p>
              </div>

              <div className="p-6 text-center bg-indigo-50 rounded-xl border border-indigo-200">
                <div className="inline-flex justify-center items-center mb-4 w-12 h-12 bg-indigo-100 rounded-full">
                  <MessageCircle className="w-6 h-6 text-indigo-600" />
                </div>
                <h4 className="mb-2 text-lg font-semibold text-gray-900 font-sora">Support Français</h4>
                <p className="text-sm text-gray-600 font-rubik">Assistance en français</p>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="p-8 mb-12 bg-white rounded-2xl shadow-xl md:p-12">
            <h2 className="mb-8 text-3xl font-bold text-center text-gray-900 font-sora">
              Questions Fréquentes
            </h2>
            
            <div className="space-y-6">
              <div className="p-6 bg-gray-50 rounded-xl">
                <div className="flex items-start">
                  <div className="flex-shrink-0 mr-4">
                    <div className="flex justify-center items-center w-8 h-8 bg-purple-100 rounded-full">
                      <Lightbulb className="w-5 h-5 text-purple-600" />
                    </div>
                  </div>
                  <div>
                    <h3 className="mb-2 text-lg font-semibold text-gray-900 font-sora">
                      Comment créer mon compte Burok ?
                    </h3>
                    <p className="text-gray-600 font-rubik">
                      Téléchargez l'application depuis l'App Store ou Google Play, puis suivez les étapes d'inscription. 
                      Vous recevrez un email de confirmation pour activer votre compte.
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-6 bg-gray-50 rounded-xl">
                <div className="flex items-start">
                  <div className="flex-shrink-0 mr-4">
                    <div className="flex justify-center items-center w-8 h-8 bg-blue-100 rounded-full">
                      <CheckCircle className="w-5 h-5 text-blue-600" />
                    </div>
                  </div>
                  <div>
                    <h3 className="mb-2 text-lg font-semibold text-gray-900 font-sora">
                      Mes données sont-elles sécurisées ?
                    </h3>
                    <p className="text-gray-600 font-rubik">
                      Absolument ! Nous utilisons un chiffrement de niveau bancaire (AES-256) et respectons le RGPD. 
                      Vos données sont protégées par les plus hauts standards de sécurité.
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-6 bg-gray-50 rounded-xl">
                <div className="flex items-start">
                  <div className="flex-shrink-0 mr-4">
                    <div className="flex justify-center items-center w-8 h-8 bg-green-100 rounded-full">
                      <ArrowRight className="w-5 h-5 text-green-600" />
                    </div>
                  </div>
                  <div>
                    <h3 className="mb-2 text-lg font-semibold text-gray-900 font-sora">
                      Comment synchroniser mes appareils ?
                    </h3>
                    <p className="text-gray-600 font-rubik">
                      Connectez-vous avec le même compte sur tous vos appareils. La synchronisation se fait automatiquement 
                      lorsque vous êtes connecté à Internet.
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-6 bg-gray-50 rounded-xl">
                <div className="flex items-start">
                  <div className="flex-shrink-0 mr-4">
                    <div className="flex justify-center items-center w-8 h-8 bg-orange-100 rounded-full">
                      <AlertCircle className="w-5 h-5 text-orange-600" />
                    </div>
                  </div>
                  <div>
                    <h3 className="mb-2 text-lg font-semibold text-gray-900 font-sora">
                      J'ai oublié mon mot de passe, que faire ?
                    </h3>
                    <p className="text-gray-600 font-rubik">
                      Sur l'écran de connexion, cliquez sur "Mot de passe oublié". Vous recevrez un email avec les instructions 
                      pour réinitialiser votre mot de passe en toute sécurité.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Types of Support */}
          <div className="p-8 bg-white rounded-2xl shadow-xl md:p-12">
            <h2 className="mb-8 text-3xl font-bold text-center text-gray-900 font-sora">
              Types d'Assistance
            </h2>
            
            <div className="grid gap-6 md:grid-cols-2">
              <div className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
                <div className="mb-4">
                  <div className="inline-flex justify-center items-center w-12 h-12 bg-blue-100 rounded-full">
                    <MessageCircle className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
                <h3 className="mb-3 text-xl font-semibold text-gray-900 font-sora">Support Technique</h3>
                <ul className="space-y-2 text-gray-600 font-rubik">
                  <li>• Problèmes de connexion</li>
                  <li>• Bugs et dysfonctionnements</li>
                  <li>• Installation et mise à jour</li>
                  <li>• Synchronisation des données</li>
                </ul>
              </div>

              <div className="p-6 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl border border-purple-200">
                <div className="mb-4">
                  <div className="inline-flex justify-center items-center w-12 h-12 bg-purple-100 rounded-full">
                    <Users className="w-6 h-6 text-purple-600" />
                  </div>
                </div>
                <h3 className="mb-3 text-xl font-semibold text-gray-900 font-sora">Support Utilisateur</h3>
                <ul className="space-y-2 text-gray-600 font-rubik">
                  <li>• Guide d'utilisation</li>
                  <li>• Gestion du compte</li>
                  <li>• Questions sur les fonctionnalités</li>
                  <li>• Conseils et bonnes pratiques</li>
                </ul>
              </div>
            </div>

            {/* Contact Section */}
            <div className="p-8 mt-12 text-center bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl border border-purple-200">
              <h3 className="mb-4 text-2xl font-bold text-gray-900 font-sora">
                Vous ne trouvez pas de réponse ?
              </h3>
              <p className="mb-6 text-gray-600 font-rubik">
                Notre équipe support est là pour vous aider personnellement
              </p>
              <a
                href="mailto:entreprise@burok.app"
                className="inline-flex items-center px-8 py-4 font-semibold text-white bg-gradient-to-r from-purple-600 to-indigo-600 rounded-full transition-all duration-200 hover:shadow-lg hover:scale-105 font-rubik"
              >
                <Mail className="mr-3 w-5 h-5" />
                Écrire à l'équipe support
              </a>
              <p className="mt-4 text-sm text-gray-500 font-rubik">
                Réponse garantie sous 24h • Lundi au Vendredi
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Support;
